import React, {
	useEffect,
	useMemo,
	useState,
} from 'react';
import isEqual from 'lodash.isequal';
import Space from 'antd/lib/space';
import Tag from 'antd/lib/tag';
import Table from 'antd/lib/table';
import Button from 'antd/lib/button';
import { PlusOutlined } from '@ant-design/icons/lib/icons';
import { formatCurrency } from '@shared/utils/currency';
import { Currencies } from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { calculateTotal } from '@shared/utils/math';
import type {
	TransformedEUAInvoices,
	TransformedHireInvoices,
} from '@api/features/voyages/getVoyageDetails';
import Select from '@client/components/Select';
import CurrencyInput from '@client/components/CurrencyInput';
import styles from './styles/PaymentInvoiceInput.module.css';

type MinimalInvoice = { id: number; amount?: number; outstanding?: number; currency?: string };

const getNewRow = () => ({
	key: Math.random(),
	dirty: false,
	hireInvoiceId: -1,
});

type Row = {
	key: string | number;
	dirty: boolean;
	hireInvoiceId: number | null;
	amount?: number | null;
	originalAmount?: number | null;
};

const getInitialState = (value: Value[] | undefined) => {
	if (value != null && value.length > 0) {
		return value.map((val) => ({
			key: Math.random(),
			dirty: true,
			hireInvoiceId: val.hireInvoiceId,
			amount: val.amount,
			originalAmount: val.amount,
		}));
	}

	return [getNewRow()];
};

const statesAreEqual = (value: Value[] | undefined, internalRows: Row[] | undefined) => {
	if (value && value.length > 0) {
		const initialState = value.map((val) => ({
			hireInvoiceId: val.hireInvoiceId,
			amount: val.amount,
			originalAmount: val.amount,
		}));

		const transformedRows = internalRows?.map(({ hireInvoiceId, amount, originalAmount }) => ({
			hireInvoiceId,
			amount,
			originalAmount,
		}));

		return isEqual(initialState, transformedRows);
	}

	return true;
};

type Value = { hireInvoiceId: number | null; amount: number | null };

type Props = {
	hireInvoices: TransformedHireInvoices | TransformedEUAInvoices;
	totalAmount: number | null;
	currency: Values<typeof Currencies>;
	value?: Value[];
	onChange?: (values: Value[] | undefined) => void;
};

const PaymentInvoiceInput = ({
	hireInvoices,
	totalAmount,
	onChange,
	currency,
	value,
}: Props) => {
	const [initialTotalAmount, setInitialTotalAmount] = useState(totalAmount);
	const [internalRows, setInternalRows] = useState<Row[] | undefined>(getInitialState(value));

	const updateAmount = (rowKey: string | number) => (newAmount: number | null) => {
		setInternalRows((oldRows) => oldRows?.map((row) => {
			if (row.key !== rowKey) {
				return row;
			}

			return {
				...row,
				amount: newAmount,
				dirty: true,
			};
		}));
	};

	const updateInvoiceId = (rowKey: string | number) => (newInvoiceId: number | null) => {
		setInternalRows((oldRows) => oldRows?.map((row) => (row.key === rowKey ? ({
			...row,
			hireInvoiceId: newInvoiceId,
		}) : row)));
	};

	useEffect(() => {
		if (
			statesAreEqual(value, internalRows) &&
			totalAmount === initialTotalAmount
		) {
			return;
		}

		if (internalRows?.every((row) => row.hireInvoiceId === -1)) {
			onChange?.([{
				hireInvoiceId: -1,
				amount: totalAmount,
			}]);

			return;
		}

		if (
			internalRows?.length === 1 &&
			internalRows[0].hireInvoiceId != null &&
			internalRows[0]?.hireInvoiceId > 0
		) {
			const [row] = internalRows;

			onChange?.([{
				hireInvoiceId: row.hireInvoiceId,
				amount: totalAmount,
			}]);

			return;
		}

		onChange?.(internalRows?.map((row) => ({
			hireInvoiceId: row.hireInvoiceId,
			amount: row.amount ?? null,
		})));
	}, [internalRows, totalAmount]);

	const selectedInvoices = useMemo(() => (
		internalRows?.map((row) => row.hireInvoiceId)
	), [internalRows]);

	const addRow = () => setInternalRows((oldRows) => [
		...(oldRows ?? []),
		getNewRow(),
	]);

	const allocatedAmountByInvoiceId = useMemo(() => {
		const invoiceMap = new Map<number, number>();

		hireInvoices.forEach((invoice) => {
			const relatedRows = internalRows?.filter(
				(r) => r.hireInvoiceId === invoice.id && r.amount != null,
			) ?? [];

			if (relatedRows.length) {
				const total = calculateTotal(relatedRows, (r) => r.amount ?? 0);

				if (total) {
					invoiceMap.set(invoice.id, total);
				}
			}
		});

		return invoiceMap;
	}, [hireInvoices, internalRows]);

	const columns = [
		{
			title: 'Invoice',
			width: 200,
			render: (_: any, record: Row) => (
				<Select
					key={record.key}
					className={styles.invoiceInput}
					value={record.hireInvoiceId}
					onChange={updateInvoiceId(record.key)}
					placeholder="Invoice"
					showSearch
					allowClear={internalRows && internalRows?.length > 1}
					onClear={() => {
						setInternalRows(internalRows?.filter((r) => r.key !== record.key));
						setInitialTotalAmount(0);
					}}
					popupMatchSelectWidth={false}
					optionLabelProp="children"
					optionFilterProp="label"
					size="middle"
					options={[
						{ label: 'No invoice', value: -1, children: (<div>No invoice</div>) },
						...hireInvoices.map((inv) => ({
							label: `INV-${inv.invoiceNumber}`,
							value: inv.id,
							disabled: selectedInvoices?.includes(inv.id),
							children: (
								<div className={styles.invoiceWrapper}>
									<Space>
										<Tag color="blue">{`INV-${inv.invoiceNumber}`}</Tag>
										<span style={{ fontSize: 12, color: '#888' }}>
											{formatCurrency(inv.amount, currency)}
											{' '}
											invoiced
										</span>
									</Space>
								</div>
							),
						})),
					]}
				/>
			),
		},
		{
			title: 'Outstanding',
			width: 120,
			render: (_: any, record: Row) => {
				const inv = (hireInvoices as MinimalInvoice[]).find((i) => i.id === record.hireInvoiceId);

				return inv?.outstanding != null ? (
					<Tag color="orange">{formatCurrency(inv.outstanding, currency)}</Tag>
				) : null;
			},
		},
		...(internalRows && internalRows?.length > 1 ?
			[
				{
					title: 'Amount',
					width: 170,
					render: (_: any, record: Row) => (
						<CurrencyInput
							currency={currency}
							className={styles.amountInput}
							size="middle"
							value={record.amount}
							onChange={updateAmount(record.key)}
							allowNegative
						/>
					),
				},
			] :
			[]),
		{
			title: 'Balance',
			width: 120,
			render: (_: any, record: Row) => {
				if (!record.hireInvoiceId) {
					return null;
				}

				const inv = (hireInvoices as MinimalInvoice[]).find((i) => i.id === record.hireInvoiceId);

				if (!inv) {
					return null;
				}

				const outstanding = Math.abs(inv.outstanding ?? 0);
				const allocated =
					internalRows && internalRows?.length > 1 ?
						Math.abs(allocatedAmountByInvoiceId.get(record.hireInvoiceId) ?? 0) :
						Math.abs(totalAmount ?? 0);
				const remaining = outstanding - allocated;

				return (
					<Tag color={remaining > 0 ? 'red' : 'green'}>
						{formatCurrency(remaining, currency)}
					</Tag>
				);
			},
		},
	];

	return (
		<>
			<Table
				dataSource={internalRows}
				columns={columns}
				rowKey="key"
				scroll={{ x: 'max-content' }}
				pagination={false}
				size="small"
			/>
			{internalRows && internalRows.length !== hireInvoices.length + 1 && (
				<Button onClick={addRow} type="link" className={styles.addButton} icon={(<PlusOutlined />)}>
					Split payment
				</Button>
			)}
		</>
	);
};

export default PaymentInvoiceInput;
